import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Clapperboard, Sparkles, CalendarDays, ListChecks, BarChart3, Copy, Plus, Trash2, Zap } from "lucide-react";
import { CampaignSelect } from "../components/CampaignSelect";

const TABS = [
  ["generate", "Générateur IA", Sparkles],
  ["gallery", "Galerie affiches", Clapperboard],
  ["calendar", "Calendrier 14 j", CalendarDays],
  ["checklist", "Checklist + Hooks", ListChecks],
  ["perf", "Suivi perfs", BarChart3],
];

export default function ContentStudioModule() {
  const [tab, setTab] = useState("generate");
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="content-studio">
      <div className="flex items-center gap-3 mb-1">
        <Clapperboard className="w-7 h-7 text-[#D90429]" />
        <h1 className="text-3xl font-black tracking-tight">🎬 Studio Contenu</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Génère du contenu viral (Reels/TikTok/FB) pour le dojo — légendes IA, calendrier, checklist et suivi.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${tab === k ? "bg-[#D90429] text-white" : "bg-secondary/40 hover:bg-secondary"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "generate" && <GeneratorTab />}
      {tab === "gallery" && <GalleryTab />}
      {tab === "calendar" && <ReferenceTab which="calendar" />}
      {tab === "checklist" && <ReferenceTab which="checklist" />}
      {tab === "perf" && <PerfTab />}
    </div>
  );
}

function GeneratorTab() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
  const [ideasText, setIdeasText] = useState("Enfant timide avant/après 3 mois\nAdulte débutant qui teste son 1er cours de kickboxing\nTrop vieux pour commencer le BJJ ?");
  const [count, setCount] = useState(8);
  const [busy, setBusy] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [restored, setRestored] = useState(false);
  // Affiche IA
  const [posterIdea, setPosterIdea] = useState("");
  const [posterHeadline, setPosterHeadline] = useState("");
  const [posterFormat, setPosterFormat] = useState("story");
  const [posterBusy, setPosterBusy] = useState(false);
  const [posterUrl, setPosterUrl] = useState(null);
  // Programmation
  const [scheduleDate, setScheduleDate] = useState("");
  const [schedulingIdx, setSchedulingIdx] = useState(null);
  // Variantes d'accroche
  const [variants, setVariants] = useState([]);
  const [variantsBusy, setVariantsBusy] = useState(false);
  // Affiche par légende
  const [capPosters, setCapPosters] = useState({});   // idx -> image_url
  const [capPosterBusy, setCapPosterBusy] = useState(null);
  // Programmation en lot
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchStart, setBatchStart] = useState("");
  const [batchSpacing, setBatchSpacing] = useState(1);
  const [batchBusy, setBatchBusy] = useState(false);
  // Aperçu façon réseau social
  const [previewCap, setPreviewCap] = useState(null);   // {c, idx}

  // Reprendre la dernière génération + la dernière affiche
  useEffect(() => {
    api.get("/content-studio/captions/latest").then((r) => {
      if (r.data?.captions?.length) {
        setCaptions(r.data.captions);
        if (r.data.ideas?.length) setIdeasText(r.data.ideas.join("\n"));
        setRestored(true);
      }
    }).catch(() => {});
    api.get("/content-studio/posters/latest").then((r) => {
      if (r.data?.image_url) setPosterUrl(r.data.image_url);
    }).catch(() => {});
  }, []);

  const generate = async () => {
    const ideas = ideasText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!ideas.length) return toast.error("Ajoute au moins une idée de vidéo (1 par ligne).");
    setBusy(true);
    setCaptions([]);
    try {
      const { data } = await api.post("/content-studio/generate-captions/start", { ideas, count: Number(count), campaign_id: campaignId });
      const jobId = data.job_id;
      toast.info("Génération en cours… (jusqu'à 1 min)");
      // Sondage du job (évite le timeout du proxy sur les longs appels IA)
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 180000) {
          setBusy(false);
          return toast.error("Délai dépassé, réessaie.");
        }
        try {
          const { data: st } = await api.get(`/content-studio/generate-captions/job/${jobId}`);
          if (st.status === "done") {
            setCaptions(st.captions || []);
            toast.success(`${st.count} légendes générées ✨`);
            setBusy(false);
          } else if (st.status === "error") {
            toast.error(st.error || "Erreur de génération");
            setBusy(false);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (e) {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération");
      setBusy(false);
    }
  };

  const copyCap = (c) => {
    const txt = `${c.caption_full || [c.hook, c.body, c.cta].filter(Boolean).join("\n\n")}\n\n${(c.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(txt);
    toast.success("Légende copiée 📋");
  };

  const generatePoster = async () => {
    if (!posterIdea.trim() && !posterHeadline.trim())
      return toast.error("Donne une idée ou une accroche pour l'affiche.");
    setPosterBusy(true);
    setPosterUrl(null);
    try {
      const { data } = await api.post("/content-studio/poster/start", {
        idea: posterIdea, headline: posterHeadline, format: posterFormat, campaign_id: campaignId,
      });
      const jobId = data.job_id;
      toast.info("Création de l'affiche… (jusqu'à 1 min)");
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 180000) { setPosterBusy(false); return toast.error("Délai dépassé, réessaie."); }
        try {
          const { data: st } = await api.get(`/content-studio/poster/job/${jobId}`);
          if (st.status === "done") { setPosterUrl(st.image_url); toast.success("Affiche prête ✨"); setPosterBusy(false); }
          else if (st.status === "error") { toast.error(st.error || "Erreur"); setPosterBusy(false); }
          else setTimeout(poll, 3000);
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur"); setPosterBusy(false);
    }
  };

  const scheduleCaption = async (c, idx) => {
    const caption_full = `${c.caption_full || [c.hook, c.body, c.cta].filter(Boolean).join("\n\n")}\n\n${(c.hashtags || []).join(" ")}`;
    setSchedulingIdx(idx);
    try {
      const { data } = await api.post("/content-studio/schedule-to-autopilot", {
        caption_full,
        scheduled_at: scheduleDate ? new Date(scheduleDate).toISOString() : null,
        image_url: capPosters[idx] || posterUrl || null,
        campaign_id: campaignId,
      });
      toast.success("Envoyée au Pilote Auto — à valider 🛡️", {
        description: data.needs_image ? "Ajoute une affiche avant d'approuver (image requise pour publier)." : "Approuve-la dans « Pilote Auto Réseaux »." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    }
    setSchedulingIdx(null);
  };

  const fetchVariants = async () => {
    const idea = posterIdea.trim() || posterHeadline.trim();
    if (!idea) return toast.error("Écris d'abord une idée d'affiche.");
    setVariantsBusy(true);
    try {
      const { data } = await api.post("/content-studio/headline-variants", { idea });
      setVariants(data.variants || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setVariantsBusy(false);
  };

  const pollPoster = (jobId, onDone, onErr) => {
    const started = Date.now();
    const poll = async () => {
      if (Date.now() - started > 180000) return onErr("Délai dépassé");
      try {
        const { data: st } = await api.get(`/content-studio/poster/job/${jobId}`);
        if (st.status === "done") onDone(st.image_url);
        else if (st.status === "error") onErr(st.error || "Erreur");
        else setTimeout(poll, 3000);
      } catch { setTimeout(poll, 3000); }
    };
    setTimeout(poll, 3000);
  };

  const generateCaptionPoster = async (c, idx) => {
    const head = (c.hook || c.type || "Cours d'essai (19,98 $)").slice(0, 60);
    setCapPosterBusy(idx);
    try {
      const { data } = await api.post("/content-studio/poster/start", {
        idea: c.hook || c.body || c.type || "", headline: head, format: "story", campaign_id: campaignId,
      });
      toast.info("Affiche de la légende en création…");
      pollPoster(data.job_id,
        (url) => { setCapPosters((m) => ({ ...m, [idx]: url })); toast.success("Affiche prête ✨"); setCapPosterBusy(null); },
        (err) => { toast.error(err); setCapPosterBusy(null); });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setCapPosterBusy(null); }
  };

  const scheduleBatch = async () => {
    if (!captions.length) return toast.error("Aucune légende à programmer.");
    setBatchBusy(true);
    try {
      const items = captions.map((c, i) => ({
        caption_full: `${c.caption_full || [c.hook, c.body, c.cta].filter(Boolean).join("\n\n")}\n\n${(c.hashtags || []).join(" ")}`,
        image_url: capPosters[i] || posterUrl || null,
      }));
      const { data } = await api.post("/content-studio/schedule-batch", {
        items,
        start_date: batchStart ? new Date(batchStart).toISOString() : null,
        spacing_days: Number(batchSpacing),
        campaign_id: campaignId,
      });
      toast.success(`${data.scheduled} légendes programmées 🛡️`, {
        description: `Du ${String(data.first).slice(0, 10)} au ${String(data.last).slice(0, 10)} · à valider dans le Pilote Auto` });
      setBatchOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBatchBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-3">
        <label className="text-xs text-muted-foreground block">Idées de vidéos (1 par ligne)</label>
        <textarea data-testid="ideas-input" value={ideasText} onChange={(e) => setIdeasText(e.target.value)} rows={8}
          className="w-full px-3 py-2 rounded bg-secondary/30 border border-border text-sm" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted-foreground">Nb de légendes</label>
          <input data-testid="count-input" type="number" min={1} max={30} value={count} onChange={(e) => setCount(e.target.value)}
            className="w-20 px-3 py-2 rounded bg-secondary/30 border border-border text-sm" />
        </div>
        <CampaignSelect
          value={campaignId}
          onChange={setCampaignId}
          label="Campagne (Planificateur)"
          labelClassName="text-xs text-muted-foreground block mb-1"
          className="w-full px-3 py-2 rounded bg-secondary/30 border border-border text-sm text-foreground"
          testid="cstudio-campaign-select"
        />
        <button data-testid="generate-btn" onClick={generate} disabled={busy}
          className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          <Zap className="w-4 h-4" /> {busy ? "Génération en cours…" : "Générer les légendes"}
        </button>
        <p className="text-[11px] text-muted-foreground">Structure Hook → Valeur → CTA + hashtags. Modèle IA : GPT-5.2 (clé Universelle).</p>

        {/* ─────────── Générateur d'AFFICHE IA ─────────── */}
        <div className="mt-4 pt-4 border-t border-border space-y-2" data-testid="poster-generator">
          <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="w-4 h-4 text-amber-400" /> Affiche visuelle IA</div>
          <input data-testid="poster-idea" value={posterIdea} onChange={(e) => setPosterIdea(e.target.value)} placeholder="Idée / thème (ex: cours d'essai enfants BJJ)"
            className="w-full px-3 py-2 rounded bg-secondary/30 border border-border text-sm" />
          <input data-testid="poster-headline" value={posterHeadline} onChange={(e) => setPosterHeadline(e.target.value)} placeholder="Accroche à afficher (ex: cours d'essai (19,98 $))"
            className="w-full px-3 py-2 rounded bg-secondary/30 border border-border text-sm" />
          <div className="flex items-center gap-2">
            <button data-testid="headline-variants-btn" onClick={fetchVariants} disabled={variantsBusy}
              className="text-xs px-2.5 py-1 rounded bg-secondary/40 hover:bg-secondary border border-border disabled:opacity-50">
              💡 {variantsBusy ? "…" : "3 variantes d'accroche"}
            </button>
          </div>
          {variants.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="headline-variants">
              {variants.map((v, i) => (
                <button key={i} data-testid={`variant-${i}`} onClick={() => { setPosterHeadline(v); toast.success("Accroche choisie"); }}
                  className="text-[11px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25">
                  {v}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {[["story", "Story/Reels 9:16"], ["feed", "Feed 1:1"]].map(([v, l]) => (
              <button key={v} data-testid={`poster-format-${v}`} onClick={() => setPosterFormat(v)}
                className={`flex-1 text-xs py-1.5 rounded border ${posterFormat === v ? "bg-amber-500 text-zinc-950 border-amber-500 font-semibold" : "bg-secondary/30 border-border"}`}>{l}</button>
            ))}
          </div>
          <button data-testid="generate-poster-btn" onClick={generatePoster} disabled={posterBusy}
            className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {posterBusy ? "Création…" : "Générer l'affiche"}
          </button>
          {posterUrl && (
            <div className="space-y-2" data-testid="poster-preview">
              <img src={mediaUrl(posterUrl)} alt="Affiche IA" className="w-full rounded-lg border border-border" />
              <a href={mediaUrl(posterUrl)} target="_blank" rel="noreferrer" download data-testid="poster-download"
                className="block text-center text-xs py-1.5 rounded bg-secondary/40 hover:bg-secondary">⬇️ Télécharger l'affiche</a>
              <p className="text-[10px] text-muted-foreground">Cette affiche sera jointe aux légendes que tu envoies au Pilote Auto.</p>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-2 space-y-3" data-testid="captions-list">
        {restored && captions.length > 0 && (
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded px-3 py-1.5" data-testid="restored-banner">
            ↩️ Dernière génération reprise automatiquement.
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-muted-foreground">📅 Programmer le</label>
          <input type="datetime-local" data-testid="schedule-date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
            className="px-2 py-1 rounded bg-secondary/30 border border-border text-xs" />
          <span className="text-[10px] text-muted-foreground">(vide = demain 14h30)</span>
          {captions.length > 1 && (
            <button data-testid="batch-open-btn" onClick={() => setBatchOpen((o) => !o)}
              className="ml-auto text-xs px-3 py-1.5 rounded bg-emerald-600/80 hover:bg-emerald-600 text-white font-semibold">
              📆 Programmer tout en lot
            </button>
          )}
        </div>
        {batchOpen && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-3 space-y-2" data-testid="batch-panel">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <label className="text-muted-foreground">Début</label>
              <input type="datetime-local" data-testid="batch-start" value={batchStart} onChange={(e) => setBatchStart(e.target.value)}
                className="px-2 py-1 rounded bg-secondary/30 border border-border" />
              <label className="text-muted-foreground">Écart (jours)</label>
              <input type="number" min={0} max={30} data-testid="batch-spacing" value={batchSpacing} onChange={(e) => setBatchSpacing(e.target.value)}
                className="w-16 px-2 py-1 rounded bg-secondary/30 border border-border" />
            </div>
            <p className="text-[10px] text-muted-foreground">Les {captions.length} légendes seront programmées 1 par jour d'écart choisi, chacune avec son affiche si générée. Toutes arrivent en « À valider ».</p>
            <button data-testid="batch-schedule-btn" onClick={scheduleBatch} disabled={batchBusy}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg font-semibold text-sm disabled:opacity-50">
              {batchBusy ? "Programmation…" : `Programmer les ${captions.length} légendes`}
            </button>
          </div>
        )}
        {captions.length === 0 && <p className="text-sm text-muted-foreground">Tes légendes générées apparaîtront ici.</p>}
        {captions.map((c, i) => (
          <div key={i} className="border border-border rounded-lg p-4 bg-secondary/10" data-testid={`caption-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#D90429]/15 text-[#D90429] font-semibold">{c.type || "Légende"}</span>
              <div className="flex items-center gap-3">
                <button onClick={() => copyCap(c)} data-testid={`copy-${i}`} className="text-xs flex items-center gap-1 hover:text-[#D90429]"><Copy className="w-3.5 h-3.5" /> Copier</button>
                <button onClick={() => generateCaptionPoster(c, i)} disabled={capPosterBusy === i} data-testid={`caption-poster-${i}`} className="text-xs flex items-center gap-1 text-amber-400 hover:text-amber-300 disabled:opacity-50">
                  <Sparkles className="w-3.5 h-3.5" /> {capPosterBusy === i ? "Affiche…" : "Affiche"}
                </button>
                <button onClick={() => setPreviewCap({ c, idx: i })} data-testid={`preview-${i}`} className="text-xs flex items-center gap-1 text-sky-400 hover:text-sky-300">
                  👁️ Aperçu
                </button>
                <button onClick={() => scheduleCaption(c, i)} disabled={schedulingIdx === i} data-testid={`schedule-${i}`} className="text-xs flex items-center gap-1 text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
                  <CalendarDays className="w-3.5 h-3.5" /> {schedulingIdx === i ? "Envoi…" : "Programmer"}
                </button>
              </div>
            </div>
            {capPosters[i] && (
              <img src={mediaUrl(capPosters[i])} alt="Affiche légende" className="w-28 rounded-md border border-border mb-2" data-testid={`caption-poster-img-${i}`} />
            )}
            {c.hook && <p className="font-bold text-sm mb-2">{c.hook}</p>}
            <p className="text-sm whitespace-pre-line text-foreground/80">{c.caption_full || c.body}</p>
            {c.cta && !c.caption_full && <p className="text-sm mt-2 font-medium text-[#D90429]">{c.cta}</p>}
            {(c.hashtags || []).length > 0 && <p className="text-xs text-blue-400 mt-2">{(c.hashtags || []).join(" ")}</p>}
          </div>
        ))}
      </div>

      {/* APERÇU façon Instagram / Facebook avant envoi au Pilote Auto */}
      {previewCap && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="preview-modal" onClick={() => setPreviewCap(null)}>
          <div className="bg-white text-zinc-900 rounded-xl max-w-sm w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-zinc-200">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-red-500 to-amber-400 flex items-center justify-center text-white text-xs font-bold">K</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-tight">institut_impact_elite</div>
                <div className="text-[10px] text-zinc-500">Val-d'Or, QC</div>
              </div>
              <span className="text-zinc-400">•••</span>
            </div>
            {(capPosters[previewCap.idx] || posterUrl) ? (
              <img src={mediaUrl(capPosters[previewCap.idx] || posterUrl)} alt="Aperçu" className="w-full object-cover bg-zinc-100" data-testid="preview-image" />
            ) : (
              <div className="w-full aspect-square bg-zinc-100 flex items-center justify-center text-zinc-400 text-sm text-center px-6" data-testid="preview-noimage">
                Aucune affiche jointe.<br />Clique « Affiche » sur la légende pour en générer une.
              </div>
            )}
            <div className="flex items-center gap-4 px-3 pt-2.5 text-zinc-800 text-xl">❤️ 💬 ✈️ <span className="ml-auto">🔖</span></div>
            <div className="px-3 py-2 text-sm max-h-40 overflow-y-auto">
              <span className="font-semibold mr-1">institut_impact_elite</span>
              <span className="whitespace-pre-line">{previewCap.c.caption_full || [previewCap.c.hook, previewCap.c.body, previewCap.c.cta].filter(Boolean).join("\n\n")}</span>
              {(previewCap.c.hashtags || []).length > 0 && <div className="text-sky-600 mt-1">{(previewCap.c.hashtags || []).join(" ")}</div>}
            </div>
            <div className="flex gap-2 p-3 border-t border-zinc-200">
              <button onClick={() => setPreviewCap(null)} className="flex-1 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-sm font-semibold" data-testid="preview-close">Fermer</button>
              <button onClick={() => { const p = previewCap; setPreviewCap(null); scheduleCaption(p.c, p.idx); }} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold" data-testid="preview-schedule">📅 Programmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryTab() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content-studio/posters");
      setPosters(data.posters || []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Supprimer cette affiche de la galerie ?")) return;
    try { await api.delete(`/content-studio/posters/${id}`); setPosters((p) => p.filter((x) => x.id !== id)); toast.success("Affiche supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!posters.length) return <p className="text-sm text-muted-foreground" data-testid="gallery-empty">Aucune affiche générée pour l'instant. Crée-en une dans l'onglet « Générateur IA ».</p>;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="poster-gallery">
      {posters.map((p) => (
        <div key={p.id} className="border border-border rounded-lg overflow-hidden bg-secondary/10" data-testid={`gallery-item-${p.id}`}>
          <img src={mediaUrl(p.image_url)} alt={p.headline || "Affiche"} className="w-full aspect-[9/16] object-cover" />
          <div className="p-2 space-y-1">
            <div className="text-xs font-semibold truncate">{p.headline || p.idea || "Affiche"}</div>
            <div className="text-[10px] text-muted-foreground">{String(p.created_at || "").slice(0, 10)} · {p.format === "feed" ? "Feed 1:1" : "Story 9:16"}</div>
            <div className="flex gap-1.5 pt-1">
              <a href={mediaUrl(p.image_url)} target="_blank" rel="noreferrer" download className="flex-1 text-center text-[11px] py-1 rounded bg-secondary/40 hover:bg-secondary" data-testid={`gallery-download-${p.id}`}>⬇️</a>
              <button onClick={() => del(p.id)} data-testid={`gallery-delete-${p.id}`} className="flex-1 text-center text-[11px] py-1 rounded bg-rose-900/40 text-rose-300 hover:bg-rose-900/60"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferenceTab({ which }) {
  const [ref, setRef] = useState(null);
  useEffect(() => { api.get("/content-studio/reference").then((r) => setRef(r.data)).catch(() => setRef(false)); }, []);
  if (!ref) return <p className="text-sm text-muted-foreground">Chargement…</p>;

  if (which === "calendar") {
    return (
      <div className="overflow-x-auto" data-testid="calendar-table">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
            <th className="py-2 px-2">Jour</th><th className="px-2">Idée</th><th className="px-2">Titre du Reel</th><th className="px-2">Type</th><th className="px-2">Objectif</th>
          </tr></thead>
          <tbody>
            {ref.calendar.map((d) => (
              <tr key={d.jour} className="border-b border-border/50" data-testid={`cal-row-${d.jour}`}>
                <td className="py-2 px-2 font-bold">J{d.jour}</td>
                <td className="px-2 text-foreground/80">{d.idee}</td>
                <td className="px-2 font-medium">{d.titre}</td>
                <td className="px-2"><span className="text-[10px] px-2 py-0.5 rounded bg-secondary">{d.type}</span></td>
                <td className="px-2 text-xs text-muted-foreground">{d.objectif}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div data-testid="checklist-block">
        <h3 className="font-bold mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#D90429]" /> Checklist tournage (10 points)</h3>
        <ol className="space-y-2 text-sm">
          {ref.checklist.map((c, i) => (
            <li key={i} className="flex gap-2"><span className="text-[#D90429] font-bold">{i + 1}.</span> {c}</li>
          ))}
        </ol>
      </div>
      <div data-testid="hooks-block">
        <h3 className="font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D90429]" /> 20 hooks prêts à l'emploi</h3>
        <ul className="space-y-1.5 text-sm text-foreground/80">
          {ref.hooks.map((h, i) => (<li key={i} className="border-l-2 border-[#D90429]/40 pl-2">« {h} »</li>))}
        </ul>
      </div>
    </div>
  );
}

function PerfTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), platform: "Instagram", titre: "", type_contenu: "", vues: 0, saves: 0, clics: 0, essais_generes: 0 });
  const load = () => api.get("/content-studio/performance").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.titre.trim()) return toast.error("Mets un titre de Reel.");
    try {
      await api.post("/content-studio/performance", { ...form, vues: Number(form.vues), saves: Number(form.saves), clics: Number(form.clics), essais_generes: Number(form.essais_generes) });
      toast.success("Ligne ajoutée");
      setForm({ ...form, titre: "", vues: 0, saves: 0, clics: 0, essais_generes: 0 });
      load();
    } catch { toast.error("Erreur"); }
  };
  const del = async (id) => { await api.delete(`/content-studio/performance/${id}`); load(); };

  return (
    <div data-testid="perf-tab">
      <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-lg bg-secondary/20 border border-border">
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-2 py-1.5 rounded bg-background border border-border text-sm" data-testid="perf-date" />
        <input placeholder="Titre du Reel" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="px-2 py-1.5 rounded bg-background border border-border text-sm flex-1 min-w-[140px]" data-testid="perf-titre" />
        <input type="number" placeholder="Vues" value={form.vues} onChange={(e) => setForm({ ...form, vues: e.target.value })} className="w-20 px-2 py-1.5 rounded bg-background border border-border text-sm" data-testid="perf-vues" title="Vues" />
        <input type="number" placeholder="Saves" value={form.saves} onChange={(e) => setForm({ ...form, saves: e.target.value })} className="w-20 px-2 py-1.5 rounded bg-background border border-border text-sm" title="Sauvegardes" />
        <input type="number" placeholder="Essais" value={form.essais_generes} onChange={(e) => setForm({ ...form, essais_generes: e.target.value })} className="w-20 px-2 py-1.5 rounded bg-background border border-border text-sm" title="Essais générés" data-testid="perf-essais" />
        <button onClick={add} data-testid="perf-add-btn" className="bg-[#D90429] text-white px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Ajouter</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
            <th className="py-2 px-2">Date</th><th className="px-2">Titre</th><th className="px-2 text-right">Vues</th><th className="px-2 text-right">Saves</th><th className="px-2 text-right">Essais</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">Aucune donnée encore.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50">
                <td className="py-2 px-2">{r.date}</td>
                <td className="px-2 font-medium">{r.titre}</td>
                <td className="px-2 text-right">{r.vues}</td>
                <td className="px-2 text-right">{r.saves}</td>
                <td className="px-2 text-right font-bold text-[#D90429]">{r.essais_generes}</td>
                <td className="px-2 text-right"><button onClick={() => del(r.id)} className="text-muted-foreground hover:text-[#D90429]"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
